import { StyledButton } from "@aureskonnect/react-ui";
import { CATALOGUE_MODE, ELECTRON_MODE, WEBVIEW_MODE } from "@constants";
import { Card } from "@mui/material";
import swal from "@sweetalert/with-react";
import { t } from "i18next";
import { useSnapshot } from "valtio";

import React from "react";
import Modal from "react-modal";

import { setIsAppPaused, store } from "@store";

import { handleLedControl } from "@helpers/general";

import { KeyboardWrapper } from "@components/SyncModal/KeyboardWrapper";
import { MiddlewareApiUrlInputCard } from "@components/SyncModal/MiddlewareApiUrlInputWrapper";

import {
    kioskStore,
    setIsLocalConfigModalOpened,
    setIsLocalSettingModalOpened,
    setIsMenuModalOpened,
} from "@pages/Kiosk/store";

enum systemActionsType {
    NoAction = "NoAction",
    Reset = "reset",
    ChangeMiddleware = "Change Middleware",
    Active = "pause",
    Update = "update",
    Restart = "restart",
    Close = "close",
    SwitchOff = "switch off",
}
type ConfirmationSystemActionsType = {
    setSystemActions: React.Dispatch<React.SetStateAction<systemActionsType>>;
    systemActions: systemActionsType;
    setIsSystemConfirmationOpen: React.Dispatch<React.SetStateAction<boolean>>;
    isSystemConfirmationOpen: boolean;
};

export default function ConfirmationSystemActionsModelWrapper({
    setSystemActions,
    systemActions,
    setIsSystemConfirmationOpen,
    isSystemConfirmationOpen,
}: ConfirmationSystemActionsType): JSX.Element {
    const { isLedActive, connectedLedPort, isKeyboardOpened } =
        useSnapshot(kioskStore);
    const { isAppPaused } = useSnapshot(store);

    const middlewareApiUrl = JSON.parse(
        localStorage.getItem("middlewareApiUrl") as string
    );
    const shopDetail = JSON.parse(localStorage.getItem("shopDetail") as string);
    const selectedShopApp = JSON.parse(
        localStorage.getItem("selectedShopApp") as string
    );

    const [isInputValueInvalid, setIsInputValueInvalid] = React.useState(false);
    const [isFieldRequired, setIsFieldRequired] = React.useState(false);
    const [isUpdateMiddlware, setIsUpdateMiddlware] = React.useState(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const keyboard = React.useRef<any>(null);

    function handleSystemActionOnclickEvent() {
        switch (systemActions) {
            case "reset":
                console.log(
                    "[System Action] Resetting kiosk: clearing localStorage and reloading"
                );
                localStorage.clear();
                location.reload();
                break;

            case "Change Middleware":
                console.log(
                    "[System Action] Changing middleware address: opening middleware update modal"
                );
                setIsUpdateMiddlware(true);
                break;

            case "pause":
                console.log(
                    "[System Action] Pausing application: closing confirmation modal and changing app status"
                );
                setIsSystemConfirmationOpen(false);
                handleChangeApplicationStatus();
                break;

            case "update":
                console.log("[System Action] Updating application");
                if (process.env.REACT_APP_MODE === ELECTRON_MODE) {
                    console.log(
                        "[System Action] Electron mode detected: sending update event to Electron API"
                    );
                    (window as RendererWindow).electronAPI.sendUpdateEvent();
                }
                setSystemActions(systemActionsType.NoAction);
                setIsSystemConfirmationOpen(false);
                break;

            case "restart":
                console.log("[System Action] Restarting post");
                setSystemActions(systemActionsType.NoAction);
                setIsSystemConfirmationOpen(false);

                if (process.env.REACT_APP_MODE === WEBVIEW_MODE) {
                    console.log(
                        "[System Action] Webview mode detected: triggering 'restart_poste'"
                    );
                    useSystemAction("restart_poste");
                } else if (process.env.REACT_APP_MODE === ELECTRON_MODE) {
                    console.log(
                        "[System Action] Electron mode detected: sending restart event to Electron API"
                    );
                    (window as RendererWindow).electronAPI.sendRestartEvent();
                }
                break;

            case "close":
                console.log("[System Action] Closing app");

                if (isLedActive) {
                    console.log(
                        "[System Action] LED is active: disabling LED before exit"
                    );
                    handleLedControl(connectedLedPort, "Disable");
                }

                setSystemActions(systemActionsType.NoAction);
                setIsSystemConfirmationOpen(false);

                if (process.env.REACT_APP_MODE === WEBVIEW_MODE) {
                    console.log(
                        "[System Action] Webview mode detected: triggering 'close_app'"
                    );
                    useSystemAction("close_app");
                } else if (process.env.REACT_APP_MODE === ELECTRON_MODE) {
                    console.log(
                        "[System Action] Electron mode detected: sending quit event to Electron API"
                    );
                    (window as RendererWindow).electronAPI.sendQuitEvent();
                }
                break;

            case "switch off":
                console.log("[System Action] Switching off post");

                setSystemActions(systemActionsType.NoAction);
                setIsSystemConfirmationOpen(false);

                if (process.env.REACT_APP_MODE === WEBVIEW_MODE) {
                    console.log(
                        "[System Action] Webview mode detected: triggering 'shutDown_poste'"
                    );
                    useSystemAction("shutDown_poste");
                } else if (process.env.REACT_APP_MODE === ELECTRON_MODE) {
                    console.log(
                        "[System Action] Electron mode detected: sending shutdown event to Electron API"
                    );
                    (window as RendererWindow).electronAPI.sendShutdownEvent();
                }
                break;

            default:
                console.warn(
                    `[System Action] Unknown or no action: "${systemActions}"`
                );
                setSystemActions(systemActionsType.NoAction);
                setIsSystemConfirmationOpen(false);
        }
    }

    function useSystemAction(systemAction: string): void {
        const url = `http://localhost:6009/api/v1/kiosk?systemAction=${systemAction}`;
        fetch(url).catch((error) =>
            console.log(
                `Error while using system action with action: "${systemAction}" and Error: "${error}"`
            )
        );
    }

    function handleChangeApplicationStatus() {
        const previousAppPaused = !isAppPaused;
        setIsAppPaused(!isAppPaused);
        setIsMenuModalOpened(false);
        swal({
            icon: "success",
            content: <p>{t("Application Status changed successfully")}!</p>,
            buttons: false,
            timer: 2000,
        });
        setIsLocalConfigModalOpened(false);
        setIsLocalSettingModalOpened(false);
        setSystemActions(systemActionsType.NoAction);
        if (isLedActive && previousAppPaused) {
            handleLedControl(connectedLedPort, "Orange");
        }

        const url = `${middlewareApiUrl}/ModifyKioskState`;
        fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                franchiseId: shopDetail.franchise_uid,
                shopId: shopDetail.shop_uid,
                appId: selectedShopApp.appId,
                etat: isAppPaused ? "In line" : "Standby state",
            }),
        })
            // eslint-disable-next-line
            .then((response): Promise<any> => response.json())
            .then((data): void => {
                if (data.error) {
                    console.log(
                        `Error on change Kiosk application status in the cloud with data message: ${data.message}`
                    );
                    return;
                }
                console.log(
                    `Changing Kiosk application status with success in the cloud`
                );
            })
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .catch((error: any) => {
                console.log(
                    `Catch error on changing kiosk application with error: ${error}`
                );
            });
    }

    return (
        <Modal
            isOpen={isSystemConfirmationOpen}
            ariaHideApp={false}
            style={{
                overlay: {
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "none",
                    zIndex: 3,
                },
                content: {
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    padding: "0px",
                    color: "black",
                    height: systemActions !== "pause" ? "30rem" : "36rem",
                    width: "37rem",
                    zIndex: 2,
                    backgroundColor: "#075d9b",
                    border: "1px solid #075d9b",
                    bottom: "unset",
                    top: "unset",
                    right: "unset",
                    left: "unset",
                    zoom: `${
                        process.env.REACT_APP_MODE === CATALOGUE_MODE
                            ? "50%"
                            : "100%"
                    }`,
                    borderRadius: "20px",
                },
            }}
        >
            {!isUpdateMiddlware ? (
                <ConfirmationDystemAction
                    setIsSystemConfirmationOpen={setIsSystemConfirmationOpen}
                    handleSystemActionOnclickEvent={
                        handleSystemActionOnclickEvent
                    }
                    systemActions={systemActions}
                />
            ) : (
                <div className="d-flex justify-content-center">
                    <MiddlewareApiUrlInputCard
                        isInputValueInvalid={isInputValueInvalid}
                        setIsInputValueInvalid={setIsInputValueInvalid}
                        isFieldRequired={isFieldRequired}
                        setIsSystemConfirmationOpen={
                            setIsSystemConfirmationOpen
                        }
                        setIsUpdateMiddlware={setIsUpdateMiddlware}
                        isUpdateMiddlware={isUpdateMiddlware}
                        keyboard={keyboard}
                    />
                    {isKeyboardOpened === true ? (
                        <Card
                            style={{
                                position: "fixed",
                                bottom: 0,
                                width: "98vw",
                                left: "0.5rem",
                            }}
                            data-testid="keyboard-wrapper"
                        >
                            <KeyboardWrapper
                                setIsFieldRequired={setIsFieldRequired}
                                isInputValueInvalid={isInputValueInvalid}
                                setIsInputValueInvalid={setIsInputValueInvalid}
                                keyboard={keyboard}
                            />
                        </Card>
                    ) : null}
                </div>
            )}
        </Modal>
    );
}

type ConfirmationDystemActionType = {
    setIsSystemConfirmationOpen: React.Dispatch<React.SetStateAction<boolean>>;
    handleSystemActionOnclickEvent: () => void;
    systemActions: systemActionsType;
};

function ConfirmationDystemAction({
    setIsSystemConfirmationOpen,
    handleSystemActionOnclickEvent,
    systemActions,
}: ConfirmationDystemActionType): JSX.Element {
    const { isAppPaused } = useSnapshot(store);
    function handleCloseModal() {
        setIsSystemConfirmationOpen(false);
    }
    return (
        <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{
                gap: systemActions === "pause" ? "60px" : "80px",
                background: "linear-gradient(135deg, #1f2937 0%, #111827 100%)",
                padding: "3rem 2rem",
                borderRadius: "20px",
                border: "1px solid #374151",
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                position: "relative",
                overflow: "hidden",
                backdropFilter: "blur(10px)",
                minWidth: "500px",
                height: "100%",
            }}
        >
            {/* Background accent */}
            <div
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "4px",
                    background:
                        "linear-gradient(90deg, #077dbc 0%, #065a94 50%, #077dbc 100%)",
                }}
            />

            {/* Animated Play Icon with light styling */}
            {systemActions === "pause" && (
                <div
                    style={{
                        padding: "1.5rem",
                        background: "#075d9b",
                        borderRadius: "50%",
                        border: "2px solid #077dbc",
                        backdropFilter: "blur(10px)",
                        marginTop: "20px",
                        animation: "iconPulse 2s ease-in-out infinite",
                    }}
                >
                    {/* Custom animated play icon */}
                    <div
                        style={{
                            width: "60px",
                            height: "60px",
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <div
                            style={{
                                width: 0,
                                height: 0,
                                borderLeft: "20px solid #ffffff",
                                borderTop: "15px solid transparent",
                                borderBottom: "15px solid transparent",
                                marginLeft: "5px",
                                filter: "drop-shadow(0 4px 8px #077dbc)",
                                animation:
                                    "playIconGlow 1.5s ease-in-out infinite alternate",
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Title */}
            <div
                style={{
                    textAlign: "center",
                    fontSize: "32px",
                    fontWeight: "700",
                    fontFamily: "Segoe UI, sans-serif",
                    color: "#ffffff",
                    letterSpacing: "0.5px",
                    textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
                    lineHeight: "1.2",
                }}
            >
                {t("Confirmation")}
            </div>

            {/* Message */}
            <div
                style={{
                    textAlign: "center",
                    fontSize: "20px",
                    fontWeight: "400",
                    fontFamily: "Segoe UI, sans-serif",
                    color: "#d1d5db",
                    letterSpacing: "0.3px",
                    lineHeight: "1.5",
                    marginLeft: "100px",
                    marginRight: "100px",
                    opacity: 0.95,
                }}
            >
                {t(
                    `Would you like to ${
                        systemActions === "pause"
                            ? isAppPaused
                                ? "restart"
                                : systemActions
                            : systemActions
                    } your ${
                        systemActions === "restart" ||
                        systemActions === "switch off"
                            ? "post"
                            : "application"
                    } ?`
                )}
            </div>

            {/* Action Buttons */}
            <div className="d-flex" style={{ gap: "20px", marginTop: "20px" }}>
                {/* Back Button */}
                <StyledButton
                    rounded={true}
                    className="m-2"
                    style={{
                        width: "179px",
                        height: "61px",
                        fontSize: "16px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "12px",
                        border: "1px solid #e5e7eb",
                        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
                        transition: "all 0.2s ease",
                        background: "#ffffff",
                        color: "#1f2937",
                        fontFamily: "Segoe UI, sans-serif",
                        letterSpacing: "0.5px",
                        cursor: "pointer",
                    }}
                    onClick={handleCloseModal}
                >
                    {t("Back")}
                </StyledButton>

                {/* Confirm Button */}
                <StyledButton
                    rounded={true}
                    className="m-2"
                    style={{
                        width: "233px",
                        height: "61px",
                        fontSize: "16px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "12px",
                        borderRadius: "12px",
                        border: "none",
                        boxShadow: "0 10px 25px -5px rgba(7, 125, 188, 0.4)",
                        transition: "all 0.2s ease",
                        background:
                            "linear-gradient(135deg, #077dbc 0%, #075d9b 100%)",
                        color: "#ffffff",
                        fontFamily: "Segoe UI, sans-serif",
                        letterSpacing: "0.5px",
                        cursor: "pointer",
                    }}
                    onClick={handleSystemActionOnclickEvent}
                >
                    {t("Confirm")}
                </StyledButton>
            </div>
            <style>{`
    @keyframes modalFadeIn {
        from {
            opacity: 0;
            transform: scale(0.95) translateY(20px);
        }
        to {
            opacity: 1;
            transform: scale(1) translateY(0);
        }
    }
    
    @keyframes iconPulse {
        0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 20px rgba(255, 255, 255, 0.1);
        }
        50% {
            transform: scale(1.05);
            box-shadow: 0 0 30px rgba(255, 255, 255, 0.2);
        }
    }
    
    @keyframes playIconGlow {
        0% {
            filter: drop-shadow(0 4px 8px rgba(255, 255, 255, 0.3));
            opacity: 0.9;
        }
        100% {
            filter: drop-shadow(0 6px 12px rgba(255, 255, 255, 0.5));
            opacity: 1;
        }
    }
    
    .confirmation-modal {
        animation: modalFadeIn 0.3s ease-out;
    }
    
    .modern-button:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba(7, 125, 188, 0.3);
    }
`}</style>
        </div>
    );
}
